import React, { memo } from 'react';
import { BlurView } from 'expo-blur';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { useUniqueNativeID } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCardCarouselDots = memo((props: Props) => {
  const { items, activeIndex } = props;

  const uniqueId = useUniqueNativeID('product-card-carousel-dots');

  return (
    <View style={styles.container}>
      {items.map((_, index) => (
        <ProductCardCarouselDot key={`${uniqueId}-${index}`} isActive={activeIndex === index} />
      ))}
    </View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const ProductCardCarouselDot = (props: DotProp) => {
  const { isActive } = props;

  const dotStyles = [styles.dot, isActive ? styles.dotActive : styles.dotIdle];

  return (
    <View>
      <BlurView intensity={BLUR_INTENSITY} style={styles.dotBlurView} />
      <View style={dotStyles} aria-selected={isActive} />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DOT_SIZE = theme.spacing['2'];
const BLUR_INTENSITY = 45;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 0,
    bottom: theme.spacing['6'],
    left: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },

  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE,
  },
  dotActive: {
    backgroundColor: theme.colors.WHITE,
  },
  dotIdle: {
    backgroundColor: theme.colors.OPACITY.WHITE.DARKER,
  },
  dotBlurView: {
    ...StyleSheet.absoluteFill,
    borderRadius: DOT_SIZE,
    overflow: 'hidden',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  items: unknown[];
  activeIndex: number;
};

type DotProp = {
  isActive: boolean;
};
